import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import api from "@/lib/api";
import {
  Wallet as WalletIcon, TrendingUp, Users, Zap,
  ArrowDownToLine, ArrowUpFromLine, Lock, Sparkles,
  Inbox, ChevronRight, Award, ExternalLink, Loader2, CheckCircle2,
  Copy, Check, Share2, Coins, History
} from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useNavigate } from "react-router-dom";
import { formatNumber } from "@/lib/web3";
import { useAuth } from "@/contexts/AuthContext";
import { useWallet } from "@/contexts/WalletContext";
import { toast } from "sonner";
import { LevelIncomeTable } from "@/components/LevelIncomeTable";
import { LeadershipRewardTable } from "@/components/LeadershipRewardTable";
import { ROIClaimWidget } from "@/components/ROIClaimWidget";

const GOLD = "#FFD36A";
const RED = "#EF4444";
const GREEN = "#22C55E";

function KpiCard({ icon: Icon, label, value, sub, subLabel, subColor = "#22C55E", iconColor = GOLD, accentBg = "rgba(255,211,106,0.12)", testid, variant = "kpi-gold", valueColor = GOLD }) {
  return (
    <div className={`card-3d lift-on-hover p-4 sm:p-5 relative h-full flex flex-col ${variant}`} data-testid={testid}>
      <div className="shimmer-line" />
      <div className="flex items-center justify-between mb-2 relative gap-2">
        <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] leading-tight">{label}</div>
        <div className="icon-pill-3d w-8 h-8 flex items-center justify-center shrink-0" style={{ background: accentBg, color: iconColor }}>
          <Icon size={14} />
        </div>
      </div>
      <div className="mono text-xl sm:text-2xl font-semibold relative truncate" style={{ color: valueColor, textShadow: `0 0 24px ${valueColor}33` }}>{value}</div>
      {(sub || subLabel) && (
        <div className="flex items-baseline gap-1 mt-1 text-[11px] relative">
          <span className="text-[var(--text-3)]">{subLabel}</span>
          <span className="mono truncate" style={{ color: subColor }}>{sub}</span>
        </div>
      )}
    </div>
  );
}

// 6-tier Fortune G style packages (matches backend)
const PACKAGES = [
  { name: "Starter",  amount: 20,    tone: "#22C55E", badge: null },
  { name: "Basic",    amount: 50,    tone: "#3B82F6", badge: null },
  { name: "Standard", amount: 100,   tone: "#A78BFA", badge: null },
  { name: "Advanced", amount: 250,   tone: "#F59E0B", badge: null },
  { name: "Premium",  amount: 500,   tone: "#EC4899", badge: "Popular" },
  { name: "Elite",    amount: 1000,  tone: "#FFD36A", badge: "Max" },
];

// Custom-amount limits (must match backend CUSTOM_MIN/MAX in routes/wallet.py)
const CUSTOM_MIN = 20;
const CUSTOM_MAX = 100000;

export default function Dashboard() {
  const [s, setS] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openDeposit, setOpenDeposit] = useState(false);
  const [pkg, setPkg] = useState("Starter");
  const [customAmount, setCustomAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [txStatus, setTxStatus] = useState(""); // idle | initiating | sending | confirming | confirmed
  const [txHash, setTxHash] = useState("");
  const [txError, setTxError] = useState("");
  const [confirmations, setConfirmations] = useState(0);
  const [minConfirmations, setMinConfirmations] = useState(1);
  const [stakingActivated, setStakingActivated] = useState(null);
  const [showRecover, setShowRecover] = useState(false);
  const [recoverHash, setRecoverHash] = useState("");
  const [recoverBusy, setRecoverBusy] = useState(false);
  const [recoverMsg, setRecoverMsg] = useState("");
  const [levelStatus, setLevelStatus] = useState(null);
  const [leadership, setLeadership] = useState(null);
  const [stakingRefreshKey, setStakingRefreshKey] = useState(0);
  const [refLink, setRefLink] = useState(() => {

    try {

      if (typeof window === "undefined") return "";

      const u = JSON.parse(localStorage.getItem("umakant_user") || "null");

      if (!u) return "";

      const code = u.referral_code || u.address || u.email;

      return code ? `${window.location.origin}/?ref=${code}` : "";

    } catch (e) { return ""; }

  });
  const [refCopied, setRefCopied] = useState(false);
  const [roiHistory, setRoiHistory] = useState({ entries: [], loading: true, total_claimed: 0 });
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const wallet = useWallet();

  const load = async () => {
    try {
      const r = await api.get("/dashboard/summary");
      setS(r.data);
      // Also refresh level + leadership status whenever summary refreshes
      loadLevelStatus();
      loadLeadership();
    } finally { setLoading(false); }
  };

  const loadLevelStatus = async () => {
    try {
      const r = await api.get("/rank/level-status");
      setLevelStatus(r.data);
    } catch (e) { /* silent */ }
  };

  const loadLeadership = async () => {
    try {
      const r = await api.get("/rank/leadership");
      setLeadership(r.data);
    } catch (e) { /* silent */ }
  };

  const loadReferralLink = async () => {
    try {
      const r = await api.get("/user/referral-link");
      const origin = (typeof window !== "undefined" && window.location?.origin) || "";
      const code = r.data?.code || r.data?.address || "";
      if (code) setRefLink(`${origin}/?ref=${code}`);
    } catch (e) { /* silent */ }
  };

  const loadRoiHistory = async () => {
    try {
      setRoiHistory((p) => ({ ...p, loading: true }));
      const r = await api.get("/wallet/roi/history?limit=20");
      setRoiHistory({
        entries: r.data?.entries || [],
        total_claimed: r.data?.total_claimed || 0,
        loading: false,
      });
    } catch (e) {
      setRoiHistory({ entries: [], total_claimed: 0, loading: false });
    }
  };

  const copyReferralLink = async () => {
    if (!refLink) return;
    let copied = false;
    // Try the modern async clipboard API first (requires secure context + permission)
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(refLink);
        copied = true;
      }
    } catch { /* fall through to legacy method */ }
    // Legacy fallback: hidden textarea + execCommand (works in iframes / older mobile)
    if (!copied) {
      try {
        const ta = document.createElement("textarea");
        ta.value = refLink;
        ta.style.position = "fixed";
        ta.style.left = "-9999px";
        ta.setAttribute("readonly", "");
        document.body.appendChild(ta);
        ta.select();
        copied = document.execCommand("copy");
        document.body.removeChild(ta);
      } catch { copied = false; }
    }
    if (copied) {
      setRefCopied(true);
      toast.success("Referral link copied!", {
        description: "Share it with friends to start earning level commissions.",
      });
      setTimeout(() => setRefCopied(false), 1800);
    } else {
      toast.error("Could not copy. Long-press the link to copy manually.");
    }
  };

  useEffect(() => { load(); loadReferralLink(); loadRoiHistory(); }, []);

  // Auto-recover: when user lands on dashboard, silently check for pending deposits
  // that have a tx_hash but were never confirmed (e.g. modal closed early, RPC timeout,
  // Trust Wallet provider hang). Multi-source recovery:
  //   1. Server-side: /wallet/deposit/pending (deposits in DB with tx_hash but unconfirmed)
  //   2. Client-side: localStorage "smarthub_pending_deposit" (saved at broadcast time)
  useEffect(() => {
    let cancelled = false;
    const autoRecover = async () => {
      const tried = new Set();

      const tryVerify = async (tx_hash, deposit_id, label) => {
        if (!tx_hash || !tx_hash.startsWith("0x") || tried.has(tx_hash)) return;
        tried.add(tx_hash);
        try {
          const v = await api.post("/wallet/deposit/verify", {
            tx_hash,
            deposit_id,
            wallet_address: wallet?.address || undefined,
          });
          if (v.data?.ok && v.data?.status === "confirmed") {
            toast.success(`Deposit activated: $${v.data.amount} ${v.data.package}`, {
              description: label || "Your stake card is now live on the dashboard.",
              duration: 5000,
            });
            try { localStorage.removeItem("smarthub_pending_deposit"); } catch { /* noop */ }
            await Promise.all([load(), refreshUser()]);
          }
        } catch (e) {
          if (e?.response?.status === 409) {
            // Already credited — flush localStorage and refresh
            try { localStorage.removeItem("smarthub_pending_deposit"); } catch { /* noop */ }
            await Promise.all([load(), refreshUser()]);
          }
        }
      };

      // ── Source 1: localStorage (most reliable for Trust Wallet recoveries) ──
      try {
        const raw = localStorage.getItem("smarthub_pending_deposit");
        if (raw) {
          const local = JSON.parse(raw);
          // Drop entries older than 24 hours to avoid endless retries
          if (local.submitted_at && Date.now() - local.submitted_at > 24 * 3600 * 1000) {
            localStorage.removeItem("smarthub_pending_deposit");
          } else if (local.tx_hash) {
            await tryVerify(local.tx_hash, local.deposit_id, "Recovered from previous session.");
          }
        }
      } catch {
        /* malformed JSON — clear it */
        try { localStorage.removeItem("smarthub_pending_deposit"); } catch { /* noop */ }
      }

      if (cancelled) return;

      // ── Source 2: Backend pending deposits ──
      try {
        const r = await api.get("/wallet/deposit/pending");
        const pending = r.data?.pending || [];
        for (const dep of pending) {
          if (cancelled) return;
          await tryVerify(dep.tx_hash, dep.deposit_id);
        }
      } catch (e) { /* silent — endpoint may be unavailable */ }
    };
    // Run only after wallet connection is known (avoids early no-address calls)
    const t = setTimeout(autoRecover, 1500);
    // Re-run every 30 seconds while dashboard is open — handles the case where the user
    // is staring at the spinning loader on a slow Trust Wallet — backend confirms in
    // the background, this loop picks it up.
    const interval = setInterval(autoRecover, 30_000);
    return () => { cancelled = true; clearTimeout(t); clearInterval(interval); };
  }, [wallet?.address]);

  const resetTxState = () => {
    setTxStatus("");
    setTxHash("");
    setTxError("");
    setConfirmations(0);
    setStakingActivated(null);
  };

  const handleOpenChange = (open) => {
    setOpenDeposit(open);
    if (!open) resetTxState();
  };

  const handleDeposit = async () => {
    // Resolve amount: either a fixed package OR a user-typed custom amount
    let amt;
    let pkgName = pkg;
    if (pkg === "Custom") {
      const parsed = parseFloat(customAmount);
      if (!parsed || Number.isNaN(parsed)) {
        return toast.error("Enter a valid custom amount");
      }
      if (parsed < CUSTOM_MIN) {
        return toast.error(`Minimum deposit is $${CUSTOM_MIN} USDT`);
      }
      if (parsed > CUSTOM_MAX) {
        return toast.error(`Maximum single deposit is $${CUSTOM_MAX.toLocaleString()} USDT`);
      }
      amt = Math.round(parsed * 100) / 100; // 2-decimal precision
      pkgName = "Custom";
    } else {
      const selected = PACKAGES.find(p => p.name === pkg);
      if (!selected) return toast.error("Select a package");
      amt = selected.amount;
    }

    resetTxState();
    setBusy(true);

    try {
      // 1) Make sure wallet is connected
      if (!wallet.signer || !wallet.address) {
        toast.error("Connect your wallet first (top-right)");
        setBusy(false);
        return;
      }

      // 2) Initiate deposit on backend (gets treasury, USDT contract, chain config)
      setTxStatus("initiating");
      const initRes = await api.post("/wallet/deposit/initiate", {
        package_name: pkgName,
        amount: amt,
        wallet_address: wallet.address,
      });
      const cfg = initRes.data;
      if (cfg.min_confirmations) setMinConfirmations(cfg.min_confirmations);

      // 3) Ensure user is on BSC mainnet
      if (Number(wallet.chainId) !== Number(cfg.chain_id)) {
        toast.message("Switch to BSC", { description: "Approve network switch in your wallet" });
        const ok = await wallet.switchToBSC(false);
        if (!ok) {
          setTxError("Please switch to BSC Mainnet (Chain ID 56) and retry.");
          setBusy(false);
          return;
        }
      }

      // 4) Send the on-chain USDT transfer (MetaMask / Trust pops up)
      setTxStatus("sending");
      toast.message("Confirm in your wallet", { description: `Send ${amt} USDT to treasury on BSC` });
      const tx = await wallet.sendUSDT(
        cfg.usdt_contract,
        cfg.treasury_address,
        amt,
        cfg.usdt_decimals || 18
      );
      setTxHash(tx.hash);
      toast.success("Transaction broadcasted", { description: tx.hash.slice(0, 12) + "..." });

      // CRITICAL: Persist the tx hash to localStorage IMMEDIATELY so we can auto-recover
      // if the user closes the dialog, the browser crashes, or the wallet hangs.
      // (This is the #1 reason deposits "vanish" on Trust Wallet — its tx.wait() hangs.)
      try {
        const pending = {
          tx_hash: tx.hash,
          deposit_id: cfg.deposit_id,
          wallet_address: wallet.address,
          amount: amt,
          package_name: pkgName,
          submitted_at: Date.now(),
        };
        localStorage.setItem("smarthub_pending_deposit", JSON.stringify(pending));
      } catch { /* localStorage may be disabled */ }

      // 5) DO NOT call tx.wait() — Trust Wallet's in-app RPC frequently hangs there forever.
      //    Instead, jump straight to backend verify-poll. The backend polls BSC public RPCs
      //    directly to verify confirmation, which is reliable across all wallets.
      setTxStatus("confirming");

      // 6) Ask backend to verify + activate + run commissions; poll until confirmed
      let attempts = 0;
      const maxAttempts = 80; // 80 * 3s = 240s (4 min) — handles slow public BSC RPCs
      while (attempts < maxAttempts) {
        attempts += 1;
        try {
          const vres = await api.post("/wallet/deposit/verify", {
            tx_hash: tx.hash,
            deposit_id: cfg.deposit_id,
            wallet_address: wallet.address,
          });
          if (vres.data.ok && vres.data.status === "confirmed") {
            setTxStatus("confirmed");
            setConfirmations(vres.data.confirmations);
            // Capture staking payload from backend (authoritative)
            setStakingActivated(vres.data.staking || null);

            // Clear pending deposit from localStorage (it's now confirmed)
            try { localStorage.removeItem("smarthub_pending_deposit"); } catch { /* noop */ }

            const stk = vres.data.staking || {};
            const dailyAmt = stk.daily_roi_amount ?? (amt * 0.01);
            const capAmt = stk.cycle_cap ?? (amt * 2);
            const capMul = stk.cap_x ?? 2;

            // Headline confirmation toast
            toast.success(`Deposit confirmed · ${pkgName === "Custom" ? `Custom $${amt}` : pkgName} activated`, {
              description: `Block #${vres.data.block_number} · $${amt} credited`,
            });
            // Dedicated "STAKING STARTED" celebratory toast — gives instant visible confirmation.
            setTimeout(() => {
              toast.success(`🔥 Staking Activated! $${dailyAmt.toFixed(2)} daily ROI`, {
                description: `Cap: $${capAmt.toFixed(0)} (${capMul}X) · Next board in 24h`,
                duration: 6000,
              });
            }, 600);
            // Instant balance refresh
            await Promise.all([load(), refreshUser()]);
            setStakingRefreshKey((k) => k + 1);
            // Keep polling user state for 15s in case commissions take a moment to flow
            for (let k = 0; k < 5; k++) {
              await new Promise(r => setTimeout(r, 3000));
              await refreshUser();
              setStakingRefreshKey((k2) => k2 + 1);
            }
            // Keep the "Staking Success" panel visible inside the dialog for 4 sec,
            // then close and scroll user to the live ROI widget.
            setTimeout(() => {
              setOpenDeposit(false);
              resetTxState();
              const el = document.querySelector('[data-testid="roi-claim-widget"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 4000);
            return;
          }
          if (vres.data.status === "confirming") {
            setConfirmations(vres.data.confirmations || 0);
          }
        } catch (e) {
          const detail = e?.response?.data?.detail || "";
          // If it's an "already credited" 409, treat as success
          if (e?.response?.status === 409) {
            setTxStatus("confirmed");
            toast.success("Deposit already credited");
            try { localStorage.removeItem("smarthub_pending_deposit"); } catch { /* noop */ }
            await Promise.all([load(), refreshUser()]);
            setStakingRefreshKey((k) => k + 1);
            setTimeout(() => {
              setOpenDeposit(false);
              const el = document.querySelector('[data-testid="roi-claim-widget"]');
              if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
            }, 1200);
            return;
          }
          // Keep polling for a few attempts on transient errors
          if (attempts >= maxAttempts) {
            throw e;
          }
        }
        // Update progress hint every 10 polls (~30 sec) so user knows we're alive
        if (attempts % 10 === 0) {
          toast.message(`Still verifying on BSC… (${Math.floor((attempts * 3) / 60)}m ${(attempts * 3) % 60}s)`, {
            description: "Don't close — your USDT is already on-chain; we're waiting for full confirmation.",
            duration: 3500,
          });
        }
        await new Promise(r => setTimeout(r, 3000));   // 3 sec polling (BSC block time ~3s)
      }
      // After 4 minutes — give user clear next steps. tx_hash is still saved in localStorage,
      // so reopening the dashboard will auto-recover.
      setTxError(
        "Verification is taking longer than expected (4+ min). Your USDT has reached the treasury — " +
        "we'll auto-credit on next dashboard reload. You can safely close this window."
      );
    } catch (e) {
      const msg = e?.response?.data?.detail || e?.shortMessage || e?.message || "Deposit failed";
      setTxError(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  // Manual recovery — user pastes a tx_hash to credit a past on-chain deposit
  // that didn't auto-confirm in the UI (e.g. network timeout, modal closed early).
  const handleRecover = async () => {
    const hash = (recoverHash || "").trim();
    if (!hash.startsWith("0x") || hash.length !== 66) {
      setRecoverMsg("Please paste a valid BSC transaction hash (66 chars starting with 0x).");
      return;
    }
    setRecoverBusy(true);
    setRecoverMsg("");
    try {
      // Poll verify up to 60 sec — on-chain tx confirmation might still be propagating
      let lastErr = "";
      for (let i = 0; i < 20; i++) {
        try {
          const r = await api.post("/wallet/deposit/verify", {
            tx_hash: hash,
            wallet_address: wallet?.address || undefined,
          });
          if (r.data?.ok && r.data?.status === "confirmed") {
            setRecoverMsg(`Success! $${r.data.amount} credited for ${r.data.package}.`);
            toast.success("Deposit recovered & credited!");
            await Promise.all([load(), refreshUser()]);
            setTimeout(() => {
              setShowRecover(false);
              setRecoverHash("");
              setRecoverMsg("");
              setOpenDeposit(false);
            }, 2000);
            return;
          }
          // still confirming → keep polling
          lastErr = r.data?.reason || "Awaiting on-chain confirmation…";
          setRecoverMsg(lastErr);
        } catch (e) {
          if (e?.response?.status === 409) {
            setRecoverMsg("This deposit was already credited.");
            await Promise.all([load(), refreshUser()]);
            return;
          }
          lastErr = e?.response?.data?.detail || e?.message || "Verify failed";
          setRecoverMsg(lastErr);
        }
        await new Promise(r => setTimeout(r, 3000));
      }
      setRecoverMsg(`Timed out after 60s. Last status: ${lastErr || "unknown"}. The tx may still credit shortly — please retry.`);
    } finally {
      setRecoverBusy(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-48 bg-white/5" />
        <div className="grid grid-cols-2 gap-3">
          {[1,2,3,4].map(i => <Skeleton key={i} className="h-28 bg-white/5" />)}
        </div>
      </div>
    );
  }

  const shortRef = s?.referrer_address ? `${s.referrer_address.slice(0,6)}...${s.referrer_address.slice(-4)}` : "-";
  const referrerDisplay = s?.referrer_name || s?.referrer_email || shortRef;
  const userDisplayName = s?.name || s?.email?.split("@")[0] || "BackOffice";
  const joinedSince = s?.joined_at ? new Date(s.joined_at).toLocaleDateString() : "-";

  return (
    <div className="space-y-5">
      {/* Welcome / BackOffice card with 3D rotating coin */}
      <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
        className="card-3d glow-gold lift-on-hover p-5 sm:p-6 relative overflow-hidden"
        data-testid="dashboard-welcome-card"
      >
        <div className="shimmer-line" />
        <div className="sparkle-layer"><i /><i /><i /><i /><i /></div>
        <div className="absolute -top-6 -right-2 coin-3d-wrap pointer-events-none opacity-95">
          <div className="coin-3d w-20 h-20 sm:w-28 sm:h-28 rounded-full" style={{
            background: "radial-gradient(circle at 35% 30%, #FFE9A8 0%, #F3BA2F 45%, #A06900 100%)",
            boxShadow: "inset -6px -8px 20px rgba(0,0,0,0.45), inset 8px 8px 18px rgba(255,255,255,0.30), 0 12px 28px rgba(243,186,47,0.45)",
          }} />
        </div>
        <div className="relative">
          <div className="text-xs uppercase tracking-[0.18em]" style={{ color: GOLD }}>Welcome Back</div>
          <div className="text-2xl sm:text-3xl font-semibold mt-1 gradient-text-anim" data-testid="dashboard-user-name">{userDisplayName}</div>
          <div className="flex items-center gap-2 mt-2">
            <span className="w-2 h-2 rounded-full bg-[var(--success)] shadow-[0_0_8px_rgba(34,197,94,0.8)]" />
            <span className="mono text-xs text-[var(--text-2)]">#{s?.user_id ?? "--"}</span>
          </div>

          <div className="grid grid-cols-2 gap-3 mt-5">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-0.5">Rank</div>
              <Badge style={{ background: "rgba(255,211,106,0.16)", color: GOLD, border: "1px solid rgba(255,211,106,0.3)" }} className="hover:bg-transparent">{s?.rank}</Badge>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-0.5">Joined Since</div>
              <div className="text-sm text-[var(--text-2)]">{joinedSince}</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-0.5">Referrer</div>
              <div className="mono text-xs text-[var(--text-2)]">{s?.referrer_id ? `#${s.referrer_id}` : shortRef}</div>
              <div className="text-[10px] text-[var(--text-3)]">User ID</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-0.5">Package</div>
              <div className="text-sm text-[var(--text-2)]">{s?.package_name || "-"}</div>
              <div className="text-[10px]" style={{ color: s?.activated ? GREEN : "#7E8AA8" }}>{s?.activated ? "Active" : "Inactive"}</div>
            </div>
          </div>
        </div>
      </motion.div>

      {/* 4 KPI cards - dynamic colors */}
      <div className="grid grid-cols-2 gap-3 sm:gap-4 items-stretch">
        <KpiCard testid="dashboard-todays-income" icon={Zap} label="Today's Income"
          variant="kpi-gold" valueColor="#FFD36A" iconColor="#FFD36A" accentBg="rgba(255,211,106,0.16)"
          value={`$${formatNumber(s?.today_income, { minimumFractionDigits: 0 })}`}
          subLabel="Lost" sub={`$${formatNumber(s?.today_income_lost, { minimumFractionDigits: 0 })}`} subColor={RED} />
        <KpiCard testid="dashboard-total-income" icon={TrendingUp} label="Total Income"
          variant="kpi-green" valueColor="#86EFAC" iconColor="#86EFAC" accentBg="rgba(34,197,94,0.18)"
          value={`$${formatNumber(s?.total_income, { minimumFractionDigits: 4 })}`}
          subLabel="Lost" sub={`$${formatNumber(s?.total_income_lost, { minimumFractionDigits: 0 })}`} subColor={RED} />
        <KpiCard testid="dashboard-total-team" icon={Users} label="Total Team"
          variant="kpi-purple" valueColor="#C4B5FD" iconColor="#C4B5FD" accentBg="rgba(124,58,237,0.20)"
          value={formatNumber(s?.total_team, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}
          subLabel="Business" sub={`$${formatNumber(s?.team_business, { minimumFractionDigits: 0 })}`} subColor={GREEN} />
        <KpiCard testid="dashboard-direct-team" icon={Users} label="Direct Team"
          variant="kpi-blue" valueColor="#93C5FD" iconColor="#93C5FD" accentBg="rgba(59,130,246,0.18)"
          value={formatNumber(s?.direct_team, { maximumFractionDigits: 0, minimumFractionDigits: 0 })}
          subLabel="Business" sub={`$${formatNumber(s?.direct_business, { minimumFractionDigits: 0 })}`} subColor={GREEN} />
      </div>

      {/* Deposit + Withdraw */}
      <div className="grid grid-cols-2 gap-3">
        <Button
          data-testid="dashboard-deposit-button"
          onClick={() => setOpenDeposit(true)}
          className="h-12 font-semibold text-black"
          style={{ background: "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)", boxShadow: "0 10px 25px rgba(255,211,106,0.25)" }}
        >
          <ArrowDownToLine size={16} className="mr-2" /> Deposit
        </Button>
        <Button
          data-testid="dashboard-withdraw-button"
          onClick={() => navigate("/wallet")}
          variant="secondary"
          className="h-12 font-semibold bg-[var(--surface-2)] border border-white/10 hover:bg-[var(--surface-3)]"
        >
          <ArrowUpFromLine size={16} className="mr-2" /> Withdraw
        </Button>
      </div>

      {/* Referral System */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] mb-2 px-1">Referral</div>
        <div className={`card-3d lift-on-hover p-5 ${s?.activated ? "glow-green" : "glow-purple"}`} data-testid="dashboard-referral-card">
          <div className="shimmer-line" />
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ background: s?.activated ? "rgba(34,197,94,0.16)" : "rgba(124,58,237,0.16)", color: s?.activated ? GREEN : "#A78BFA" }}>
              {s?.activated ? <Sparkles size={18} /> : <Lock size={18} />}
            </div>
            <div className="flex-1">
              <div className="font-semibold">Referral System</div>
              <div className="text-xs text-[var(--text-3)] mb-3">Grow your network & earn rewards</div>
              <Badge className="hover:bg-transparent" style={s?.activated ? { background: "rgba(34,197,94,0.16)", color: "#86EFAC", border: "1px solid rgba(34,197,94,0.3)" } : { background: "rgba(124,58,237,0.16)", color: "#C4B5FD", border: "1px solid rgba(124,58,237,0.35)" }}>
                {s?.activated ? "Referral Active" : "Referral Locked"}
              </Badge>
              {!s?.activated && (
                <div className="text-xs text-[var(--text-3)] mt-3">
                  Activate your account to unlock referrals
                  <div className="mono mt-1 text-[var(--text-2)]">0 / 1 deposit</div>
                </div>
              )}
              {s?.activated && (
                <div className="mt-3 space-y-2">
                  {refLink && (
                    <div className="flex items-center gap-2 rounded-lg p-2 border border-white/10 bg-[var(--surface-1)]" data-testid="dashboard-referral-link-row">
                      <div className="mono text-[10px] sm:text-[11px] text-[var(--text-2)] truncate flex-1" data-testid="dashboard-referral-link-value">
                        {refLink}
                      </div>
                      <Button
                        size="sm"
                        onClick={copyReferralLink}
                        data-testid="dashboard-copy-referral-link-button"
                        className="h-7 px-2.5 text-[11px] font-semibold text-black shrink-0"
                        style={{
                          background: refCopied
                            ? "linear-gradient(135deg, #86EFAC 0%, #22C55E 100%)"
                            : "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)",
                        }}
                      >
                        {refCopied ? (
                          <><Check size={12} className="mr-1" /> Copied</>
                        ) : (
                          <><Copy size={12} className="mr-1" /> Copy</>
                        )}
                      </Button>
                    </div>
                  )}
                  <button
                    onClick={() => navigate("/team")}
                    className="text-xs text-[var(--purple-300)] hover:text-[var(--purple-200)] inline-flex items-center"
                    data-testid="dashboard-view-team-link"
                  >
                    <Share2 size={12} className="mr-1" /> View team & share details <ChevronRight size={12} className="ml-1" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Yield Generation */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] mb-2 px-1">Yield Generation</div>
        <div className={`card-3d lift-on-hover p-5 relative ${s?.activated ? "glow-green" : "glow-purple"}`} data-testid="dashboard-yield-card">
          <div className="shimmer-line" />
          <div className="sparkle-layer"><i /><i /><i /><i /><i /></div>
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="font-semibold">Active Cycles</div>
              <div className="text-xs text-[var(--text-3)]">{s?.activated ? "Bonus engine running" : "Make your first deposit to activate"}</div>
            </div>
            <Badge
              style={s?.activated
                ? { background: "rgba(34,197,94,0.16)", color: "#86EFAC", border: "1px solid rgba(34,197,94,0.3)" }
                : { background: "rgba(124,58,237,0.16)", color: "#C4B5FD", border: "1px solid rgba(124,58,237,0.35)" }}
              className="hover:bg-transparent"
              data-testid="dashboard-cycles-status"
            >
              {s?.activated ? "Active" : "Inactive"}
            </Badge>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="umakant-panel p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-1">Active</div>
              <div className="mono text-lg font-semibold">${formatNumber(s?.yield_total_deposit, { minimumFractionDigits: 0 })}</div>
              <div className="text-[10px] text-[var(--text-3)] mt-0.5">Total deposit</div>
            </div>
            <div className="umakant-panel p-3">
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-1">Claimable</div>
              <div className="mono text-lg font-semibold">${formatNumber(s?.yield_claimable, { minimumFractionDigits: 4 })}</div>
              <div className="text-[10px] text-[var(--text-3)] mt-0.5">Ready</div>
            </div>
          </div>
          <div className="mt-4 flex items-center justify-between bg-[var(--surface-1)] rounded-lg p-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)]">Claimable Reward</div>
              <div className="mono text-base font-semibold" style={{ color: GOLD }}>${formatNumber(s?.yield_claimable_reward, { minimumFractionDigits: 4 })}</div>
            </div>
            <Button disabled={!s?.yield_claimable_reward} size="sm" className="h-9 font-semibold text-black" style={{ background: "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)" }}>
              Claim
            </Button>
          </div>
          {!s?.yield_claimable_reward && (
            <div className="text-xs text-[var(--text-3)] mt-3 text-center">No Bonus Available</div>
          )}
        </div>
      </div>

      {/* Daily ROI Claim Widget — prominent placement */}
      <ROIClaimWidget refreshKey={stakingRefreshKey} onClaimSuccess={() => { load(); refreshUser(); loadRoiHistory(); setStakingRefreshKey(k => k + 1); }} />

      {/* Income Progress */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] mb-2 px-1">Income Progress</div>
        <div className="card-3d glow-purple lift-on-hover p-5 relative" data-testid="dashboard-income-limits-card">
          <div className="shimmer-line" />
          <div className="sparkle-layer"><i /><i /><i /><i /><i /></div>
          <div className="font-semibold mb-4">Income Limits</div>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-[var(--text-3)] uppercase tracking-[0.14em]">Total Limit</span>
                <span className="mono">${formatNumber(s?.total_income, { minimumFractionDigits: 0 })} / ${formatNumber(s?.total_limit, { minimumFractionDigits: 0 })} ({(s?.total_progress || 0).toFixed(0)}%)</span>
              </div>
              <Progress value={s?.total_progress || 0} className="h-2 bg-white/10" />
              <div className="bar-shimmer h-2 -mt-2 rounded-full opacity-60" />
            </div>
            <div>
              <div className="flex justify-between text-xs mb-1.5">
                <span className="text-[var(--text-3)] uppercase tracking-[0.14em]">Daily Limit</span>
                <span className="mono">${formatNumber(s?.daily_earned, { minimumFractionDigits: 0 })} / ${formatNumber(s?.daily_limit, { minimumFractionDigits: 0 })} ({(s?.daily_progress || 0).toFixed(0)}%)</span>
              </div>
              <Progress value={s?.daily_progress || 0} className="h-2 bg-white/10" />
              <div className="bar-shimmer h-2 -mt-2 rounded-full opacity-60" />
            </div>
          </div>
        </div>
      </div>

      {/* ROI History — daily accrual + claim log from roi_ledger */}
      <div>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--text-3)] mb-2 px-1 flex items-center justify-between">
          <span className="flex items-center gap-1.5"><History size={11} /> ROI History</span>
          {roiHistory.entries.length > 0 && (
            <span className="mono normal-case tracking-normal text-[10px]" style={{ color: "#86EFAC" }} data-testid="dashboard-roi-history-total">
              Claimed ${formatNumber(roiHistory.total_claimed, { minimumFractionDigits: 4 })}
            </span>
          )}
        </div>
        <div className="card-3d lift-on-hover relative overflow-hidden" data-testid="dashboard-roi-history">
          <div className="shimmer-line" />
          {roiHistory.loading ? (
            <div className="p-5 space-y-2">
              {[1,2,3].map(i => <Skeleton key={i} className="h-12 bg-white/5" />)}
            </div>
          ) : roiHistory.entries.length === 0 ? (
            <div className="p-8 text-center">
              <Coins size={28} className="mx-auto opacity-40 mb-2" style={{ color: "#FFD36A" }} />
              <div className="text-sm text-[var(--text-3)]">No ROI activity yet</div>
              <div className="text-[11px] text-[var(--text-3)] mt-1 opacity-70">Your daily ROI accruals & claims will show here</div>
            </div>
          ) : (
            <div className="divide-y divide-white/5 max-h-[360px] overflow-y-auto">
              {roiHistory.entries.map((e, idx) => {
                const isClaim = e.kind === "claim";
                const accent = isClaim ? "#86EFAC" : "#FFD36A";
                const bg = isClaim ? "rgba(34,197,94,0.14)" : "rgba(255,211,106,0.12)";
                let dateStr = "—";
                try {
                  if (e.created_at) {
                    const dt = new Date(e.created_at);
                    dateStr = dt.toLocaleString("en-GB", {
                      day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit",
                    });
                  }
                } catch { /* noop */ }
                return (
                  <div
                    key={`${e.kind}-${e.created_at}-${idx}`}
                    className="flex items-center gap-3 px-4 py-3"
                    data-testid={`roi-history-row-${idx}`}
                  >
                    <div
                      className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0"
                      style={{ background: bg, color: accent }}
                    >
                      {isClaim ? <ArrowDownToLine size={15} /> : <Coins size={15} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-semibold text-[var(--text-1)] truncate">{e.label}</div>
                      <div className="flex items-center gap-2 mt-0.5">
                        <span className="text-[10px] text-[var(--text-3)] mono">{dateStr}</span>
                        {!isClaim && e.rank_pct ? (
                          <span className="text-[10px] text-[var(--text-3)]">· {Number(e.rank_pct).toFixed(2)}%/d</span>
                        ) : null}
                        {isClaim && e.stakes_capped > 0 ? (
                          <Badge
                            className="hover:bg-transparent text-[8.5px] uppercase tracking-[0.12em]"
                            style={{ background: "rgba(124,58,237,0.16)", color: "#C4B5FD", border: "1px solid rgba(124,58,237,0.30)" }}
                          >
                            {e.stakes_capped} capped
                          </Badge>
                        ) : null}
                      </div>
                    </div>
                    <div className="mono text-sm font-bold shrink-0" style={{ color: accent }}>
                      +${formatNumber(e.amount, { minimumFractionDigits: 4 })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Level Income — 25-tier eligibility table */}
      <LevelIncomeTable data={levelStatus} />

      {/* Leadership Reward — 10-tier rank system (Explorer → Creator) */}
      <LeadershipRewardTable data={leadership} />

      {/* Deposit Dialog - Fortune G style */}
      <Dialog open={openDeposit} onOpenChange={handleOpenChange}>
        <DialogContent className="bg-[var(--surface-2)] border border-white/10 text-[var(--text-1)] sm:max-w-md max-h-[92vh] overflow-y-auto p-0" data-testid="deposit-dialog">
          {/* Header: icon + title + BALANCE */}
          <DialogHeader className="px-5 pt-5 pb-3 border-b border-white/5">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <div
                  className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0"
                  style={{
                    background: "linear-gradient(180deg, rgba(255,211,106,0.18) 0%, rgba(255,211,106,0.04) 100%)",
                    border: "1px solid rgba(255,211,106,0.4)",
                    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.10), 0 2px 6px rgba(0,0,0,0.18)",
                  }}
                >
                  <ArrowDownToLine size={18} className="text-[#FFD36A]" />
                </div>
                <div className="min-w-0">
                  <DialogTitle className="text-lg font-semibold leading-tight" style={{ color: "#FFD36A", textShadow: "0 0 12px rgba(255,211,106,0.35)" }}>
                    Activate Package
                  </DialogTitle>
                  <DialogDescription className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.10em] mt-0.5">
                    USDT · BEP-20 · BSC Mainnet
                  </DialogDescription>
                </div>
              </div>
              <div className="text-right shrink-0">
                <div className="text-[9px] text-[var(--text-3)] uppercase tracking-[0.14em]">Balance</div>
                <div className="mono text-base font-bold leading-tight" style={{ color: "#FFD36A" }}>
                  ${formatNumber(user?.total_deposit || 0, { minimumFractionDigits: 2 })}
                </div>
              </div>
            </div>
          </DialogHeader>

          {/* Status / Progress view */}
          {txStatus && (
            <div className="mx-5 mt-3 rounded-xl border border-white/10 bg-[var(--surface-1)] p-3" data-testid="deposit-tx-status">
              <div className="flex items-center gap-2 mb-1.5">
                {txStatus === "confirmed" ? (
                  <CheckCircle2 size={16} className="text-[var(--success)]" />
                ) : (
                  <Loader2 size={16} className="animate-spin text-[#FFD36A]" />
                )}
                <div className="text-sm font-semibold">
                  {txStatus === "initiating" && "Preparing deposit…"}
                  {txStatus === "sending" && "Waiting for wallet signature…"}
                  {txStatus === "confirming" && `Confirming on BSC… (${confirmations}/${minConfirmations})`}
                  {txStatus === "confirmed" && "Deposit confirmed!"}
                </div>
              </div>
              {txHash && (
                <a href={`https://bscscan.com/tx/${txHash}`} target="_blank" rel="noopener noreferrer"
                  className="mono text-[11px] text-[var(--purple-300)] hover:text-[var(--purple-200)] inline-flex items-center gap-1 break-all"
                  data-testid="deposit-tx-hash-link">
                  {txHash.slice(0, 16)}…{txHash.slice(-8)} <ExternalLink size={10} />
                </a>
              )}
              {txError && <div className="text-[11px] text-[var(--destructive)] mt-1.5" data-testid="deposit-tx-error">{txError}</div>}
            </div>
          )}

          {/* STAKING SUCCESS panel — shows IMMEDIATELY after deposit confirms */}
          {txStatus === "confirmed" && stakingActivated && stakingActivated.active && (
            <div
              className="mx-5 mt-3 rounded-2xl p-5 relative overflow-hidden"
              style={{
                background: "linear-gradient(180deg, rgba(34,197,94,0.18) 0%, rgba(34,197,94,0.04) 100%)",
                border: "1px solid rgba(34,197,94,0.45)",
                boxShadow: "0 0 32px rgba(34,197,94,0.30)",
                animation: "pulse 2s ease-in-out infinite",
              }}
              data-testid="staking-success-panel"
            >
              <div className="absolute top-0 left-0 right-0 h-1" style={{
                background: "linear-gradient(90deg, transparent 0%, #22C55E 50%, transparent 100%)",
              }} />

              <div className="text-center mb-3">
                <div
                  className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em]"
                  style={{ color: "#86EFAC" }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: "#22C55E", boxShadow: "0 0 10px #22C55E" }}
                  />
                  🔥 Staking Successful
                </div>
                <div className="mt-2 text-lg font-bold" style={{ color: "#FFF" }}>
                  ${stakingActivated.self_deposit?.toFixed(0)} is now staking
                </div>
              </div>

              <div className="grid grid-cols-2 gap-2.5 text-center">
                <div className="rounded-lg p-2.5 border border-white/10 bg-black/20">
                  <div className="text-[9px] uppercase tracking-[0.12em] text-[var(--text-3)] mb-1">Daily ROI</div>
                  <div className="mono text-base font-bold" style={{ color: "#FFE5A1" }}>
                    ${stakingActivated.daily_roi_amount?.toFixed(2)}/day
                  </div>
                  <div className="text-[9px] mt-0.5" style={{ color: "var(--text-3)" }}>
                    {stakingActivated.daily_roi_pct}% × ${stakingActivated.self_deposit?.toFixed(0)}
                  </div>
                </div>
                <div className="rounded-lg p-2.5 border border-white/10 bg-black/20">
                  <div className="text-[9px] uppercase tracking-[0.12em] text-[var(--text-3)] mb-1">Cap</div>
                  <div className="mono text-base font-bold" style={{ color: "#FFE5A1" }}>
                    ${stakingActivated.cycle_cap?.toFixed(0)}
                  </div>
                  <div className="text-[9px] mt-0.5" style={{ color: "var(--text-3)" }}>
                    {stakingActivated.cap_x}X limit
                  </div>
                </div>
              </div>

              <div className="mt-3 text-center text-[10px]" style={{ color: "#86EFAC" }}>
                <CheckCircle2 size={11} className="inline mr-1" />
                First ROI board ready in 24h · Compounding active
              </div>
            </div>
          )}

          {/* Form */}
          {!busy && txStatus !== "confirmed" && (
            <div className="px-5 pb-5 pt-3 space-y-4">
              <div>
                <div className="text-[10px] text-[var(--text-3)] uppercase tracking-[0.18em] mb-2.5">Choose Your Entry Package</div>
                <div className="grid grid-cols-3 gap-2.5">
                  {PACKAGES.map((p) => {
                    const selected = pkg === p.name;
                    const balance = parseFloat(user?.total_deposit || 0) || 0;
                    const insufficient = balance < p.amount;
                    return (
                      <button
                        key={p.name}
                        onClick={() => setPkg(p.name)}
                        data-testid={`package-${p.name.toLowerCase()}`}
                        className="pkg-medallion group relative text-left overflow-hidden"
                        style={{
                          "--tone": p.tone,
                          minHeight: "108px",
                          transform: selected ? "translateY(-2px)" : "translateY(0)",
                        }}
                        data-selected={selected ? "true" : "false"}
                      >
                        {/* Layer 1: Base gradient (metallic plate) */}
                        <div
                          className="absolute inset-0 rounded-[14px] pointer-events-none"
                          style={{
                            background: selected
                              ? `linear-gradient(155deg, ${p.tone}38 0%, ${p.tone}18 40%, ${p.tone}06 75%, transparent 100%), linear-gradient(180deg, var(--surface-1) 0%, var(--surface-2) 100%)`
                              : `linear-gradient(180deg, var(--surface-1) 0%, var(--surface-2) 100%)`,
                          }}
                        />
                        {/* Layer 2: Glossy diagonal shine */}
                        <div
                          className="absolute inset-0 rounded-[14px] pointer-events-none opacity-80"
                          style={{
                            background: `linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 28%, transparent 55%)`,
                            mixBlendMode: "overlay",
                          }}
                        />
                        {/* Layer 3: Bottom soft glow */}
                        {selected && (
                          <div
                            className="absolute -inset-px rounded-[15px] pointer-events-none"
                            style={{
                              background: `radial-gradient(120% 80% at 50% 110%, ${p.tone}55 0%, transparent 55%)`,
                            }}
                          />
                        )}
                        {/* Layer 4: Border + outer ring */}
                        <div
                          className="absolute inset-0 rounded-[14px] pointer-events-none"
                          style={{
                            border: selected ? `1.5px solid ${p.tone}` : `1px solid var(--card3d-border)`,
                            boxShadow: selected
                              ? `inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(0,0,0,0.10), 0 0 0 3px ${p.tone}22, 0 14px 28px -10px ${p.tone}55, 0 6px 14px -6px rgba(0,0,0,0.18)`
                              : `inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.06), 0 6px 14px -6px rgba(0,0,0,0.12), 0 2px 4px rgba(0,0,0,0.06)`,
                          }}
                        />
                        {/* Layer 5: Top inner highlight strip */}
                        <div
                          className="absolute top-0 left-2 right-2 h-[1px] rounded-full pointer-events-none"
                          style={{
                            background: `linear-gradient(90deg, transparent, ${selected ? p.tone : "rgba(255,255,255,0.6)"}, transparent)`,
                            opacity: selected ? 0.9 : 0.5,
                          }}
                        />

                        {/* Badge — top-right pill, fully inside card */}
                        {p.badge && (
                          <span
                            className="absolute top-1.5 right-1.5 z-10 text-[7px] font-extrabold uppercase px-1.5 py-[2px] rounded-full leading-none"
                            style={{
                              background: `linear-gradient(180deg, ${p.tone} 0%, ${p.tone}D0 100%)`,
                              color: "#0a0a0a",
                              boxShadow: `0 2px 5px ${p.tone}55, inset 0 1px 0 rgba(255,255,255,0.45), inset 0 -1px 0 rgba(0,0,0,0.15)`,
                              border: `1px solid ${p.tone}`,
                              letterSpacing: "0.06em",
                            }}
                          >
                            {p.badge}
                          </span>
                        )}

                        {/* Selected checkmark dot — top-left */}
                        {selected && (
                          <span
                            className="absolute top-2 left-2 w-[7px] h-[7px] rounded-full z-10"
                            style={{
                              background: p.tone,
                              boxShadow: `0 0 0 2.5px ${p.tone}22, 0 0 8px ${p.tone}AA`,
                            }}
                          />
                        )}

                        {/* Content */}
                        <div className="relative px-2.5 pt-2 pb-2.5 flex flex-col h-full">
                          {/* Name on its own line — reserves space so badge never collides */}
                          <div
                            className="text-[9px] font-bold uppercase tracking-[0.12em] mb-1 leading-none"
                            style={{
                              color: selected ? p.tone : "var(--text-3)",
                              textShadow: selected ? `0 0 6px ${p.tone}66` : "none",
                              paddingLeft: selected ? "12px" : "0",
                              paddingRight: p.badge ? "42px" : "0",
                            }}
                          >
                            {p.name}
                          </div>
                          <div
                            className="mono text-[22px] font-black leading-none mt-auto"
                            style={{
                              color: "var(--text-1)",
                              textShadow: selected
                                ? `0 1px 0 rgba(255,255,255,0.4), 0 0 14px ${p.tone}55`
                                : `0 1px 0 rgba(255,255,255,0.3)`,
                              letterSpacing: "-0.02em",
                            }}
                          >
                            ${formatNumber(p.amount, { minimumFractionDigits: 0 })}
                          </div>
                          <div className="mt-1.5 flex items-center gap-1">
                            <span
                              className="inline-block w-1 h-1 rounded-full"
                              style={{ background: insufficient ? "#F87171" : "#22C55E", boxShadow: `0 0 6px ${insufficient ? "#F87171" : "#22C55E"}` }}
                            />
                            <span
                              className="text-[8.5px] font-bold uppercase tracking-[0.10em]"
                              style={{ color: insufficient ? "#F87171" : "#22C55E" }}
                            >
                              {insufficient ? "Insufficient" : "Available"}
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })}

                  {/* CUSTOM AMOUNT tile — user can stake any amount ≥ $20 */}
                  {(() => {
                    const selected = pkg === "Custom";
                    const tone = "#FFD36A";
                    return (
                      <button
                        onClick={() => setPkg("Custom")}
                        data-testid="package-custom"
                        className="pkg-medallion group relative text-left overflow-hidden"
                        style={{
                          "--tone": tone,
                          minHeight: "108px",
                          transform: selected ? "translateY(-2px)" : "translateY(0)",
                        }}
                        data-selected={selected ? "true" : "false"}
                      >
                        <div
                          className="absolute inset-0 rounded-[14px] pointer-events-none"
                          style={{
                            background: selected
                              ? `linear-gradient(155deg, ${tone}38 0%, ${tone}18 40%, ${tone}06 75%, transparent 100%), linear-gradient(180deg, var(--surface-1) 0%, var(--surface-2) 100%)`
                              : `linear-gradient(180deg, var(--surface-1) 0%, var(--surface-2) 100%)`,
                          }}
                        />
                        <div
                          className="absolute inset-0 rounded-[14px] pointer-events-none opacity-80"
                          style={{
                            background: `linear-gradient(135deg, rgba(255,255,255,0.18) 0%, rgba(255,255,255,0.04) 28%, transparent 55%)`,
                            mixBlendMode: "overlay",
                          }}
                        />
                        {selected && (
                          <div
                            className="absolute -inset-px rounded-[15px] pointer-events-none"
                            style={{
                              background: `radial-gradient(120% 80% at 50% 110%, ${tone}55 0%, transparent 55%)`,
                            }}
                          />
                        )}
                        <div
                          className="absolute inset-0 rounded-[14px] pointer-events-none"
                          style={{
                            border: selected ? `1.5px solid ${tone}` : `1px dashed var(--card3d-border)`,
                            boxShadow: selected
                              ? `inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(0,0,0,0.10), 0 0 0 3px ${tone}22, 0 14px 28px -10px ${tone}55`
                              : `inset 0 1px 0 rgba(255,255,255,0.30), inset 0 -1px 0 rgba(0,0,0,0.06)`,
                          }}
                        />
                        <span
                          className="absolute top-1.5 right-1.5 z-10 text-[7px] font-extrabold uppercase px-1.5 py-[2px] rounded-full leading-none"
                          style={{
                            background: `linear-gradient(180deg, ${tone} 0%, ${tone}D0 100%)`,
                            color: "#0a0a0a",
                            boxShadow: `0 2px 5px ${tone}55, inset 0 1px 0 rgba(255,255,255,0.45)`,
                            border: `1px solid ${tone}`,
                            letterSpacing: "0.06em",
                          }}
                        >
                          ANY
                        </span>
                        {selected && (
                          <span
                            className="absolute top-2 left-2 w-[7px] h-[7px] rounded-full z-10"
                            style={{
                              background: tone,
                              boxShadow: `0 0 0 2.5px ${tone}22, 0 0 8px ${tone}AA`,
                            }}
                          />
                        )}
                        <div className="relative px-2.5 pt-2 pb-2.5 flex flex-col h-full">
                          <div
                            className="text-[9px] font-bold uppercase tracking-[0.12em] mb-1 leading-none"
                            style={{
                              color: selected ? tone : "var(--text-3)",
                              textShadow: selected ? `0 0 6px ${tone}66` : "none",
                              paddingLeft: selected ? "12px" : "0",
                              paddingRight: "32px",
                            }}
                          >
                            Custom
                          </div>
                          <div
                            className="mono text-[18px] font-black leading-none mt-auto"
                            style={{
                              color: "var(--text-1)",
                              textShadow: selected
                                ? `0 1px 0 rgba(255,255,255,0.4), 0 0 14px ${tone}55`
                                : `0 1px 0 rgba(255,255,255,0.3)`,
                              letterSpacing: "-0.02em",
                            }}
                          >
                            $ANY
                          </div>
                          <div className="mt-1.5 flex items-center gap-1">
                            <span
                              className="inline-block w-1 h-1 rounded-full"
                              style={{ background: tone, boxShadow: `0 0 6px ${tone}` }}
                            />
                            <span
                              className="text-[8.5px] font-bold uppercase tracking-[0.10em]"
                              style={{ color: tone }}
                            >
                              Min $20
                            </span>
                          </div>
                        </div>
                      </button>
                    );
                  })()}
                </div>

                {/* Custom amount input — visible only when Custom tile is selected */}
                {pkg === "Custom" && (
                  <div
                    className="mt-3 rounded-xl p-3"
                    style={{
                      background: "linear-gradient(180deg, rgba(255,211,106,0.06) 0%, rgba(255,211,106,0.02) 100%)",
                      border: "1px solid rgba(255,211,106,0.30)",
                    }}
                    data-testid="custom-amount-panel"
                  >
                    <div className="text-[10px] uppercase tracking-[0.14em] text-[var(--text-3)] mb-2 flex items-center justify-between">
                      <span>Enter Custom USDT Amount</span>
                      <span className="mono" style={{ color: "#FFD36A" }}>
                        ${CUSTOM_MIN} – ${CUSTOM_MAX.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-stretch gap-2">
                      <div
                        className="flex items-center px-3 rounded-lg mono text-base font-bold"
                        style={{
                          background: "rgba(255,211,106,0.10)",
                          border: "1px solid rgba(255,211,106,0.30)",
                          color: "#FFD36A",
                        }}
                      >
                        $
                      </div>
                      <Input
                        type="number"
                        inputMode="decimal"
                        min={CUSTOM_MIN}
                        max={CUSTOM_MAX}
                        step="0.01"
                        placeholder={`e.g. 150`}
                        value={customAmount}
                        onChange={(e) => setCustomAmount(e.target.value)}
                        data-testid="custom-amount-input"
                        className="flex-1 mono text-base font-semibold h-11"
                        style={{
                          background: "rgba(0,0,0,0.25)",
                          border: "1px solid rgba(255,211,106,0.20)",
                        }}
                      />
                    </div>
                    {customAmount && (
                      <div className="mt-2 grid grid-cols-3 gap-2 text-[9px] uppercase tracking-[0.10em] text-[var(--text-3)]">
                        <div className="rounded-md p-1.5 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
                          Cap (2X) <div className="mono mt-0.5 text-[12px] font-bold" style={{ color: "#FFD36A" }}>
                            ${(parseFloat(customAmount || 0) * 2).toFixed(2)}
                          </div>
                        </div>
                        <div className="rounded-md p-1.5 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
                          Daily ROI <div className="mono mt-0.5 text-[12px] font-bold" style={{ color: "#86EFAC" }}>
                            ${(parseFloat(customAmount || 0) * 0.01).toFixed(2)}
                          </div>
                        </div>
                        <div className="rounded-md p-1.5 text-center" style={{ background: "rgba(255,255,255,0.03)" }}>
                          Days to Cap <div className="mono mt-0.5 text-[12px] font-bold" style={{ color: "#C4B5FD" }}>
                            ~70 days
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <Button
                data-testid="deposit-submit-button"
                onClick={handleDeposit}
                disabled={busy || !wallet.address}
                className="w-full h-12 font-semibold text-black"
                style={{
                  background: wallet.address ? "linear-gradient(135deg, #FFD36A 0%, #F3BA2F 100%)" : "rgba(255,255,255,0.06)",
                  color: wallet.address ? "#000" : "var(--text-3)",
                  boxShadow: wallet.address ? "0 10px 25px -5px rgba(255,211,106,0.40)" : "none",
                  border: wallet.address ? "none" : "1px solid var(--card3d-border)",
                }}
              >
                {wallet.address
                  ? (pkg === "Custom"
                      ? `Deposit · Custom $${customAmount && parseFloat(customAmount) > 0 ? formatNumber(parseFloat(customAmount), { minimumFractionDigits: 0 }) : "—"}`
                      : `Select Package · ${pkg} ($${formatNumber(PACKAGES.find(p => p.name === pkg)?.amount || 0, { minimumFractionDigits: 0 })})`)
                  : "Connect Wallet First"}
              </Button>

              {/* Deposit History pill button */}
              <button
                className="w-full flex items-center justify-between gap-2 rounded-xl px-4 py-3 group hover:bg-white/[0.03] transition-colors"
                style={{
                  background: "linear-gradient(180deg, rgba(255,211,106,0.04) 0%, rgba(255,211,106,0.01) 100%)",
                  border: "1px solid rgba(255,211,106,0.30)",
                }}
                data-testid="deposit-history-button"
                onClick={() => { setOpenDeposit(false); navigate("/wallet"); }}
              >
                <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#FFD36A" }}>
                  <Inbox size={14} /> Deposit History
                </span>
                <span className="mono text-[10px] px-2 py-0.5 rounded-md uppercase tracking-[0.10em]"
                  style={{ background: "rgba(255,211,106,0.10)", color: "#FFD36A", border: "1px solid rgba(255,211,106,0.30)" }}>
                  ID {(user?.sequential_id || user?.address || "—").toString().slice(-8)}
                </span>
              </button>

              {/* Recovery: claim a past on-chain deposit that didn't auto-credit */}
              <button
                className="w-full flex items-center justify-between gap-2 rounded-xl px-4 py-3 hover:bg-white/[0.03] transition-colors"
                style={{
                  background: "linear-gradient(180deg, rgba(124,58,237,0.06) 0%, rgba(124,58,237,0.02) 100%)",
                  border: "1px solid rgba(124,58,237,0.30)",
                }}
                data-testid="deposit-recover-toggle"
                onClick={() => setShowRecover(v => !v)}
              >
                <span className="flex items-center gap-2 text-sm font-semibold" style={{ color: "#C4B5FD" }}>
                  <CheckCircle2 size={14} /> Recover Past Deposit
                </span>
                <span className="text-[10px] text-[var(--text-3)]">{showRecover ? "Hide" : "Have a tx hash?"}</span>
              </button>

              {showRecover && (
                <div className="rounded-xl border border-white/10 bg-[var(--surface-1)] p-3 space-y-2.5" data-testid="deposit-recover-panel">
                  <div className="text-[11px] text-[var(--text-3)]">
                    Paste your BSC transaction hash to manually credit a deposit that didn&apos;t auto-confirm.
                  </div>
                  <Input
                    value={recoverHash}
                    onChange={(e) => setRecoverHash(e.target.value)}
                    placeholder="0x..."
                    className="font-mono text-xs h-10 bg-[var(--surface-2)] border-white/10"
                    data-testid="deposit-recover-input"
                    disabled={recoverBusy}
                  />
                  <div className="flex gap-2">
                    <Button
                      onClick={handleRecover}
                      disabled={recoverBusy || !recoverHash}
                      className="flex-1 h-9 font-semibold text-black"
                      style={{ background: "linear-gradient(135deg, #C4B5FD 0%, #8B5CF6 100%)" }}
                      data-testid="deposit-recover-submit"
                    >
                      {recoverBusy ? (<><Loader2 size={14} className="animate-spin mr-1.5" /> Verifying…</>) : "Verify & Credit"}
                    </Button>
                    {recoverHash && (
                      <a
                        href={`https://bscscan.com/tx/${recoverHash}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center h-9 px-3 rounded-md text-[11px] border border-white/10 text-[var(--text-2)] hover:bg-white/5"
                        data-testid="deposit-recover-bscscan"
                      >
                        <ExternalLink size={12} className="mr-1" /> BscScan
                      </a>
                    )}
                  </div>
                  {recoverMsg && (
                    <div
                      className="text-[11px] mono"
                      style={{ color: recoverMsg.toLowerCase().startsWith("success") || recoverMsg.toLowerCase().includes("credited") ? "#86EFAC" : "var(--text-2)" }}
                      data-testid="deposit-recover-msg"
                    >
                      {recoverMsg}
                    </div>
                  )}
                </div>
              )}

              <div className="text-[10px] text-[var(--text-3)] text-center pt-1">
                Non-custodial · Smart contract secured · Instant activation
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
